import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  Button
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ViewFullSSP = () => {
  const { id: sspId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState(null);

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  useEffect(() => {
    if (sspId) {
      fetchFullSSP();
    }
  }, [sspId]);

  const fetchFullSSP = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${baseURL}/ssp-studio/ssps/${sspId}/view-full`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setHtml(response.data.html);
        setMetadata(response.data.metadata);
      } else {
        setError(response.data.message || 'Failed to generate full SSP view');
      }
    } catch (err) {
      console.error('Error fetching full SSP:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load full SSP');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleDownloadHTML = () => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${metadata?.ssp_name || 'SSP'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <Button
            color="secondary"
            outline
            size="sm"
            onClick={() => navigate(`/ssp-studio/ssp/${sspId}`)}
          >
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to SSP
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="mb-0">
                    <i className="mdi mdi-file-document-outline me-2"></i>
                    View Full SSP
                  </h5>
                  {metadata && (
                    <span className="text-muted" style={{ fontSize: '0.85em' }}>
                      {metadata.sections_count} sections, {metadata.controls_count} controls
                    </span>
                  )}
                </div>
                {!loading && !error && html && (
                  <div>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={handlePrint}
                      className="me-2"
                    >
                      <i className="mdi mdi-printer me-1"></i>
                      Print
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleDownloadHTML}
                    >
                      <i className="mdi mdi-download me-1"></i>
                      Download HTML
                    </Button>
                  </div>
                )}
              </div>

              {loading && (
                <div className="text-center p-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Generating full SSP document...</p>
                </div>
              )}

              {error && (
                <Alert color="danger" toggle={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {!loading && !error && html && (
                <>
                  <div className="mb-3 text-muted">
                    <small>
                      <i className="mdi mdi-information-outline me-1"></i>
                      Generated on {new Date(metadata?.generated_at).toLocaleString()}
                    </small>
                  </div>
                  <div
                    className="ssp-full-view"
                    dangerouslySetInnerHTML={{ __html: html }}
                    style={{ background: '#f5f5f5', padding: '20px', borderRadius: '4px' }}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ViewFullSSP;
